import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../supabase';
import { Icon } from './Icon.js';
import './ReportModal.css';

const ReportModal = ({ isOpen, onClose, post, comment, message, reportedUserId, reportedUsername }) => {
  const [selectedReason, setSelectedReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const reportReasons = [
    { value: 'spam', label: 'Spam or misleading content' },
    { value: 'harassment', label: 'Harassment or bullying' },
    { value: 'hate_speech', label: 'Hate speech or discrimination' },
    { value: 'inappropriate', label: 'Inappropriate or offensive content' },
    { value: 'false_info', label: 'False or misleading information' },
    { value: 'violence', label: 'Violence or threats' },
    { value: 'other', label: 'Other' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedReason) {
      setError('Please select a reason for reporting this post.');
      return;
    }

    if (selectedReason === 'other' && !customReason.trim()) {
      setError('Please provide details for your report.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError(`You must be signed in to report ${message ? 'messages' : comment ? 'comments' : 'posts'}.`);
        setIsSubmitting(false);
        return;
      }

      // Prepare report data
      const reportData = {
        reporter_user_id: user.id,
        reported_user_id: reportedUserId,
        post_id: post?.id || null,
        comment_id: comment?.id || null,
        message_id: message?.id || null,
        reason: selectedReason === 'other' ? customReason.trim() : selectedReason,
        timestamp: new Date().toISOString()
      };

      // Submit the report
      const { error: reportError } = await supabase
        .from('reports')
        .insert(reportData);

      if (reportError) {
        console.error('Report submission error:', reportError);
        setError('Failed to submit report. Please try again.');
      } else {
        setSuccess(true);
        // Close modal after a brief delay to show success message
        setTimeout(() => {
          handleClose();
        }, 2000);
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      setError('An unexpected error occurred. Please try again.');
    }

    setIsSubmitting(false);
  };

  const handleClose = () => {
    // Reset form state
    setSelectedReason('');
    setCustomReason('');
    setError('');
    setSuccess(false);
    onClose();
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="report-modal-overlay" onClick={handleClose}>
      <div className="report-modal" onClick={(e) => e.stopPropagation()}>
        <div className="report-modal-header">
          <div className="report-modal-title">
            <Icon name="AlertTriangle" className="report-modal-icon" />
            <h2>{message ? 'Report Message' : comment ? 'Report Comment' : 'Report Post'}</h2>
          </div>
          <button className="report-modal-close" onClick={handleClose}>
            <Icon name="X" className="icon--sm" />
          </button>
        </div>

        <div className="report-modal-content">
          {success ? (
            <div className="report-success">
              <Icon name="CheckCircle" className="icon--lg icon--success" />
              <h3>Report Submitted</h3>
              <p>Thank you for your report. We'll review it and take appropriate action.</p>
            </div>
          ) : (
            <>
              <div className="report-target-info">
                <p>You are reporting a {message ? 'message' : comment ? 'comment' : 'post'} by <strong>{reportedUsername}</strong></p>
              </div>

              <form onSubmit={handleSubmit} className="report-form">
                <div className="report-reasons">
                  <label className="report-form-label">
                    Why are you reporting this {message ? 'message' : comment ? 'comment' : 'post'}?
                  </label>
                  <div className="report-reasons-list">
                    {reportReasons.map((reason) => (
                      <label key={reason.value} className="report-reason-option">
                        <input
                          type="radio"
                          name="reason"
                          value={reason.value}
                          checked={selectedReason === reason.value}
                          onChange={(e) => setSelectedReason(e.target.value)}
                          className="report-reason-radio"
                        />
                        <span className="report-reason-label">{reason.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {selectedReason === 'other' && (
                  <div className="report-custom-reason">
                    <label className="report-form-label">
                      Please provide details
                    </label>
                    <textarea
                      value={customReason}
                      onChange={(e) => setCustomReason(e.target.value)}
                      placeholder={`Please explain why you're reporting this ${message ? 'message' : comment ? 'comment' : 'post'}...`}
                      className="report-custom-textarea"
                      rows="4"
                    />
                  </div>
                )}

                {error && (
                  <div className="report-error">
                    <Icon name="AlertCircle" className="icon--sm icon--danger" />
                    {error}
                  </div>
                )}

                <div className="report-modal-actions">
                  <button
                    type="button"
                    className="btn btn--tertiary"
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn--danger"
                    disabled={isSubmitting || !selectedReason}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Report'}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ReportModal;
